import { useCallback, useState } from 'react';
import type { AccountTypeToggleGroupValue } from 'ui-components';

import type { CreateListErrorType } from './productListsErrors';
import type { ProductInfo } from './productListsHelpers';

export type ModalView = 'closed' | 'save' | 'create';

export type SaveToListModalState = {
  view: ModalView;
  product: ProductInfo | null;
  initialSelectedListIds: string[];
  fromCreateFlow: boolean;
  createdListName: string | null;
  createAccountType: AccountTypeToggleGroupValue;
  isSubmitting: boolean;
  serverError: CreateListErrorType | null;
  openToken: number;
  // The dialog opened before membership was known, so its selection is a
  // placeholder waiting to be replaced by `reseedSave`.
  isMembershipPending: boolean;
};

const INITIAL_STATE: SaveToListModalState = {
  view: 'closed',
  product: null,
  initialSelectedListIds: [],
  fromCreateFlow: false,
  createdListName: null,
  createAccountType: 'personal',
  isSubmitting: false,
  serverError: null,
  openToken: 0,
  isMembershipPending: false,
};

export function useSaveToListModal() {
  const [state, setState] = useState<SaveToListModalState>(INITIAL_STATE);

  const openSave = useCallback(
    (
      product: ProductInfo,
      initialSelectedListIds: string[],
      isMembershipPending = false
    ) => {
      setState((prev) => ({
        ...INITIAL_STATE,
        view: 'save',
        product,
        initialSelectedListIds,
        isMembershipPending,
        openToken: prev.openToken + 1,
      }));
    },
    []
  );

  // Bumping `openToken` remounts the dialog so it re-seeds its checkboxes from
  // the new selection; leaving them stale would let a save be interpreted
  // against a baseline the user never saw. The remount also resets the active
  // tab and any picks made while the lookup was in flight — accepted over
  // blocking interaction, which a failed lookup would deadlock.
  const reseedSave = useCallback((initialSelectedListIds: string[]) => {
    setState((prev) =>
      prev.view === 'save' && prev.isMembershipPending
        ? {
            ...prev,
            initialSelectedListIds,
            isMembershipPending: false,
            openToken: prev.openToken + 1,
          }
        : prev
    );
  }, []);

  const openCreate = useCallback((accountType: AccountTypeToggleGroupValue) => {
    setState((prev) => ({
      ...prev,
      view: 'create',
      createAccountType: accountType,
      serverError: null,
    }));
  }, []);

  const returnToSaveAfterCreate = useCallback(
    (initialSelectedListIds: string[], createdListName: string) => {
      setState((prev) => ({
        ...prev,
        view: 'save',
        fromCreateFlow: true,
        createdListName,
        initialSelectedListIds,
        isMembershipPending: false,
        isSubmitting: false,
        serverError: null,
        openToken: prev.openToken + 1,
      }));
    },
    []
  );

  const close = useCallback(() => setState(INITIAL_STATE), []);

  const setSubmitting = useCallback((isSubmitting: boolean) => {
    setState((prev) => ({ ...prev, isSubmitting }));
  }, []);

  const setServerError = useCallback(
    (serverError: CreateListErrorType | null) => {
      setState((prev) => ({ ...prev, serverError }));
    },
    []
  );

  return {
    ...state,
    openSave,
    reseedSave,
    openCreate,
    returnToSaveAfterCreate,
    close,
    setSubmitting,
    setServerError,
  };
}
